import { ReactElement, useState } from "react";
import type { GetStaticProps, GetStaticPaths } from "next";
import NextHeadSeo from "next-head-seo";

import LayoutMain from "~/components/layouts/LayoutMain";
import {
  restApiESGetSettings,
  restApiESQuery,
  restApiGetTerms,
} from "~/utils/restApi";
import PageMargins from "~/components/styled/PageMargins";
import { Pagination } from "~/components/ui/Pagination";
import { FlexibleRow } from "~/components/flexibleContent/FlexibleRow";
import { HeaderWithFilter } from "~/components/partials/HeaderWithFilter";
import {
  WhatsOnFilter,
  FilterData,
  defaultFilterData,
} from "~/components/partials/WhatsOnFilter";
import { WhatsOnFilterListInfo } from "~/components/partials/WhatsOnFilterListInfo";
import { appConfig } from "~/config";
import { useSettingsContext } from "~/providers/SettingsContextProvider";
import WhatsOnListing from "~/components/content/WhatsOnListing";
import { Heading } from "~/components/ui/Heading";
import { useConfigContext } from "~/providers/ConfigContextProvider";

const WhatsOnFilterList = ({
  page,
  programme,
  category,
}: {
  page: number;
  programme: any;
  category: string;
}) => {
  const settings = useSettingsContext();
  const config = useConfigContext();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const [filterData, setFilterData] = useState<FilterData>(defaultFilterData);

  const term = settings?.taxonomies?.programmeCategory?.find(
    (c: any) => c.slug === category
  );

  const nextPageBase = `/whats-on/category/${term?.slug}/`;
  return (
    <>
      <NextHeadSeo
        title={`${term?.name} ${page > 1 ? `- ${page}` : ``} - What's On - ${
          config.appTitle
        }`}
        description={term?.description}
      />

      <HeaderWithFilter
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        title={term?.name ?? "What's on"}
        controlsId="whats-on-filter"
      />
      <main id="content">
        <WhatsOnFilter
          id="whats-on-filter"
          activeProgrammeCategoryId={term?.id}
          showTypeFilter={false}
          initiallyOpen={false}
          {...{
            isFilterOpen,
            filterData,
            setFilterData,
            filterQuery,
            setFilterQuery,
          }}
        />
        <div id="listing" />
        {page === 1 && term?.description && programme?.data?.length > 0 && (
          <FlexibleRow
            field="content"
            content={[
              {
                acf_fc_layout: "intro",
                intro: term?.description,
              },
            ]}
          />
        )}

        {(page !== 1 ||
          (term?.description > 0 && programme?.data?.length === 0)) && (
          <WhatsOnFilterListInfo
            id="listinfo"
            term={term}
            {...{
              isLoading: false,
              isError: false,
              queryResult: {
                items: programme?.data,
                pageTotal: programme?.pageTotal,
                postTotal: programme?.postTotal,
              },
              filterData: {
                page,
              },
            }}
          />
        )}

        {programme?.data?.length > 0 && (
          <>
            <WhatsOnListing
              keyPrefix="programme"
              items={programme?.data}
              heading={
                <PageMargins
                  spaceTop={page === 1 && !term?.description ? 6 : undefined}
                >
                  <Heading heading="h3" asTag space={8}>
                    Exhibitions/Events
                  </Heading>
                </PageMargins>
              }
              pagination={
                programme?.postTotal &&
                programme?.pageTotal && (
                  <PageMargins>
                    <Pagination
                      firstPageShowMore="Show more events"
                      mobileTopSpacing
                      {...{
                        page,
                        postTotal: programme?.postTotal,
                        pageTotal: programme?.pageTotal,
                      }}
                      paginationBaseUrl={nextPageBase}
                    />
                  </PageMargins>
                )
              }
            />
          </>
        )}
      </main>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const cats = await restApiGetTerms("programme_category", {
    perPage: 100,
  });

  return {
    paths: cats?.data?.length
      ? cats?.data.reduce((carry: string[], cat: any) => {
          carry.push(`/whats-on/category/${cat.slug}`);
          [1, 2, 3, 4].forEach((val: number) => {
            if (cat.count > appConfig.postsPerPage * val)
              carry.push(`/whats-on/category/${cat.slug}/${val + 1}`);
          });

          return carry;
        }, [])
      : [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const category = context?.params?.category
    ? Array.isArray(context?.params?.category) &&
      context?.params?.category?.length
      ? (context?.params?.category[0] as string)
      : context?.params?.category
    : "not-found";

  const page = context?.params?.category
    ? Array.isArray(context?.params?.category) &&
      context?.params?.category?.length > 1
      ? parseInt(
          context?.params?.category[
            context?.params?.category.length - 1
          ] as string
        )
      : 1
    : 1;

  const programme = await restApiESQuery({
    type: ["event", "exhibition"],
    perPage: appConfig.postsPerPage,
    offset: page > 1 ? (page - 1) * appConfig.postsPerPage : undefined,
    page,
    orderby: "meta.date_begin.date",
    order: "desc",
    dateEndCompare: "gte",
    programmeCategorySlug: category,
  });

  const frontendSettings = await restApiESGetSettings(["programmeCategory"]);
  if (!programme?.data?.length)
    return {
      props: {
        frontendSettings,
      },
      notFound: true,
      revalidate: frontendSettings?.options?.copyrightNotice ? 240 : 15,
    };

  return {
    props: {
      frontendSettings,
      programme,
      page,
      category,
    },
    revalidate: frontendSettings?.options?.copyrightNotice
      ? appConfig.revalidateInterval("mediaListing")
      : 15,
  };
};

WhatsOnFilterList.getLayout = function getLayout(page: ReactElement) {
  return <LayoutMain>{page}</LayoutMain>;
};
export default WhatsOnFilterList;
